<template>
 <div class="coupon-form-container">
    <Loader />
    <div class="coupon-input" v-for="n in parsedSettings?.coupons?.max_number || 0" :key="n">
     <InputField :id="'coupon-code-' + n" :ariaLabel="parsedTranslations.coupon_code_label + ' ' + n"
        :placeholder="parsedTranslations.coupon_input_placeholder" :minChar="minChar"
       :value="status.fields['coupon-code-' + n]?.value"
        @update:fieldValue="(val) => updateField('coupon-code-' + n, val)"
       @action:onfocus="() => onFocusField('coupon-code-' + n)"
       @action:onblur="() => onBlurField('coupon-code-' + n)" />
    </div>
   <div class="errorMessage hidden"></div>
    <div class="successMessage hidden"></div>
    <Button type="primary" @click="submitForm">
      {{ parsedTranslations['add_coupon_button_label'] }}
    </Button>
 </div>
</template>

<script setup>
import { computed, watch, reactive, onMounted } from 'vue';
import InputField from '../../components/input-field/InputField.vue';
import Button from '../../components/Button.vue';
import Loader from "../../components/loader/Loader.vue";
import { disableInputFields, disableSubmitButton, enableInputFields, enableSubmitButton, getFromLocalStorage, hideLoader, hideMessageElement, resetForm, saveToLocalStorage, showLoader, showMessageElement, validateTextField } from '../../utils/helpers';

const props = defineProps({
  custom: String,
});

const parsedCustom = computed(() => JSON.parse(props.custom));
const parsedSettings = computed(() => parsedCustom.value.settings);
const parsedCustomer = computed(() => parsedCustom.value.customer);
const parsedTranslations = computed(() => parsedCustom.value.translations);
const minChar = computed(() => parsedSettings.value?.coupons?.min_char_coupon || 0);
const couponApiUrl = import.meta.env.VITE_AZURE_FUNCTION_URL;

// Reactive status object tracking all inputs
const status = reactive({ fields: {} });

function initStatus() {
  const max = parsedSettings.value?.coupons?.max_number || 0;
  // reset fields
  status.fields = {};
  for (let i = 1; i <= max; i++) {
    const key = `coupon-code-${i}`;
    status.fields[key] = { value: '', valid: false, touched: false };
  }
  status.customer = parsedCustomer.value;
  status.translations = parsedTranslations.value || {};

  initializeFormFieldsFromLocalStorage();
}

function initializeFormFieldsFromLocalStorage() {
  const max = parsedSettings.value?.coupons?.max_number || 0;
  for (let i = 1; i <= max; i++) {
    const key = `coupon-code-${i}`;
    const storedValue = getFromLocalStorage(key);
    if (storedValue) {
      status.fields[key].value = storedValue;
      status.fields[key].valid = validateTextField(storedValue, minChar.value);
    }
  }
}

function deleteLocalStorageForFields() {
  const max = parsedSettings.value?.coupons?.max_number || 0;
  for (let i = 1; i <= max; i++) {
    const key = `coupon-code-${i}`;
    saveToLocalStorage(key, '');
  }
}

function resetStatus() {
  const max = parsedSettings.value?.coupons?.max_number || 0;
  for (let i = 1; i <= max; i++) {
    const key = `coupon-code-${i}`;
    status.fields[key] = { value: '', valid: false, touched: false };
  }
}

onMounted(() => initStatus());

watch(parsedSettings, () => initStatus());

function updateField(name, value) {
  if (!status.fields[name]) {
    status.fields[name] = { value: '', valid: false, touched: false };
  }
  status.fields[name].value = value;
  status.fields[name].valid = validateTextField(value, minChar.value);
  saveToLocalStorage(name, value);
}

function onBlurField(name) {
  if (!status.fields[name]) status.fields[name] = { value: '', valid: false, touched: false };
  status.fields[name].valid = validateTextField(status.fields[name].value, minChar.value);
}

async function submitForm() {
  await fetchCoupons();
}

async function fetchCoupons() {
  const container = document.querySelector('.coupon-form-container');
  disableSubmitButton(container.querySelector('button'));
  disableInputFields(container.querySelectorAll('.text-field input'));
  hideMessageElement(container.querySelector('.errorMessage'));
  hideMessageElement(container.querySelector('.successMessage'));

  const filteredStatus = {
    ...status,
    fields: Object.fromEntries(
      Object.entries(status.fields).filter(([key, field]) => field.valid)
    )
  };

  // if no valid coupons, show error and return
  if (Object.keys(filteredStatus.fields).length === 0) {
    showMessageElement(container.querySelector('.errorMessage'), `${parsedTranslations.value.no_valid_coupons_message}`);
    enableSubmitButton(container.querySelector('button'));
    enableInputFields(container.querySelectorAll('.text-field input'));
    return;
  }

  try {
    showLoader(container);
    const response = await fetch(couponApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredStatus),
    });

    if (response.status === 200) {
      const data = await response.json();
      if (data.processedCoupons) {
        const validCoupons = data.processedCoupons.validCoupons || [];
        const invalidCoupons = data.processedCoupons.invalidCoupons || [];

        if (validCoupons.length > 0) {
          const validCouponList = validCoupons.join(', ');
          showMessageElement(container.querySelector('.successMessage'), `${parsedTranslations.value.valid_coupons_message.replace('{{ coupons }}', validCouponList)}`);
        }
        if (invalidCoupons.length > 0) {
          const invalidCouponList = invalidCoupons.join(', ');
          showMessageElement(container.querySelector('.errorMessage'), `${parsedTranslations.value.invalid_coupons_message.replace('{{ coupons }}', invalidCouponList)}`);
        }
      } else {
        console.warn('No processed coupons data in response');
      }
    } else {
      console.error('Failed to process coupons. Status:', response.status);
    }
    resetForm(container);
    deleteLocalStorageForFields();
    resetStatus();
  } catch (error) {
    console.error('Error processing coupons:', error);
    showMessageElement(container.querySelector('.errorMessage'), `${parsedTranslations.value.error_processing_coupons_message}`);
  } finally {
    hideLoader(container);
    enableSubmitButton(container.querySelector('button'));
    enableInputFields(container.querySelectorAll('.text-field input'));
  }
}
</script>

